<!--
 * A Vue component for input of e-mail addresses.
 * This component accepts a pttern to test the e-mail address against.
 *
 * Properties
 * ==========
 * - name -
 * (Optional) The text that is used as id and name for input element.
 *
 * - description -
 * (Optional) A description that appears below the input element.
 *
 * - text -
 * (Optional) The text to show on the label above the input field.
 *
 * - state -
 * (Optional) The validation state to set from parent component. state === true => Valid state
 *                                                               state === false => Invalid state
 *
 * - invalidMessage -
 * (Optional) The text to show when state is set to false (invalid).
 *
 * - labelSize -
 * (Optional) To control the label text size. Valid values are 'lg' for large and 'sm' for small.
 *
 * - value -
 * (Optional) To set the initial value of the input element.
 *
 *
 * Events
 * ==========
 * - input -
 * This event is sent when user changes the input value.
 * The object to sent along with this event is the inserted value from input element.
 *
 * - validState -
 * This event is sent when user changes the input value.
 *
 * Usage
 * ==========
 * -You do not need to listen to the emitted input event from this component.
 * Just use the v-model in your parent component and the value will be updated
 * from this component automatically.
 *
 *-You can put the standard html input attributes, sucht as required,
 * autofocus, pattern, etc. directly from the parent component without explicit binding.
 * The custom attributes like listed above, should be bind.
 *
 *-Use the value of the "validState" prop in the parent component for your custom validation.
 * Bind to this prop as -- v-bind:validState.sync="validState" -- in the parent component.
-->
<template>
    <div>
        <x-input :name="name"
                 v-bind="$attrs"
                 v-model="computedValue"
                 :labelSize="labelSize"
                 :text="text"
                 :invalidMessage="invalidMessage"
                 :description="description"
                 :state="isValid">
        </x-input>
    </div>
</template>

<script>
    import xinput from './BaseComponents/x-input.vue';
    export default {
        inheritAttrs: false,
        name: 'email-input',

        components: { 'x-input': xinput },

        props: {
            value: { type: String, required: false, default: '' },
            name: { type: String, required: false, default: '' },
            labelSize: { type: String, required: false, default: '' },
            text: { type: String, required: false, default: 'E-mailadres' },
            invalidMessage: { type: String, required: false, default: '' },
            description: { type: String, required: false },
            validState: { type: Boolean, required: false, default: true },
            singleMode: { type: Boolean, required: false, default: false },
            pattern: [RegExp]
        },

        data() {
            return {
                isValid: null,
                emailValue: ''
            };
        },

        computed: {
            computedValue: {
                get() {
                    return this.emailValue || this.value;
                },

                set(input) {
                    this.emailValue = input;

                    if (this.pattern !== undefined && input.length > 0) {
                        let nValid = 0;
                        // Separate the e-mails with , or ;
                        const emails = input.split(/[,;| ]+/);
                        for (let email of emails) {
                            this.isValid = this.pattern.test(email);
                            if (!this.isValid) {
                                // Break the loop as soon as pattern is not valid
                                break;
                            } else {
                                nValid++;
                            }
                        }

                        if (this.isValid) {
                            this.isValid = !this.singleMode || (this.singleMode && nValid === 1);
                        }
                    } else {
                        // No need to show invalid state for empty input
                        this.isValid = input.length === 0;
                    }

                    this.$emit('input', input);
                    // Update the value of validState prop
                    this.$emit('update:validState', this.isValid);
                }

            }
        },

        watch: {
            value(newValue, oldValue) {
                if (newValue !== null && newValue !== undefined && newValue !== oldValue) {
                    this.emailValue = newValue;
                }
            }
        }
    };
</script>
